import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { 
  getPlayerFullName, 
  getPlayerTeam,
  calculatePlayerPerformanceScore
} from '../utils/playerUtils';
import { 
  calculateOverUnderLine, 
  calculateHitRate
} from '../utils/bettingUtils';
import { calculateDvpRanks } from '../utils/teamUtils';
import {
  getFilteredGames,
  getStatName,
  getMostRecentGame,
  findPlayerByName,
  getTeamPlayers,
  isFilterActive as checkFilterActive,
  convertStatCategoryForOdds
} from '../utils/playerProfileUtils';
import { getPlayerOdds } from '../utils/oddsUtils';

// Components
import StatChart from '../components/StatChart';
import PlayerSplits from '../components/PlayerSplits';
import PlayerForm from '../components/PlayerForm';
import PlayerRecentGames from '../components/PlayerRecentGames';
import PlayerHeader from '../components/PlayerHeader';
import PlayerControls from '../components/PlayerControls';
import PlayerFilters from '../components/PlayerFilters';
import PlayerStats from '../components/PlayerStats';

// Add this function near the top with other utility imports
const getPlayerImageUrl = (firstName, lastName) => {
  // Encode the name parts to handle special characters
  const encodedLastName = encodeURIComponent(lastName);
  const encodedFirstName = encodeURIComponent(firstName);
  return `https://www.nrlsupercoachstats.com/highcharts/PlayerPictures/2025/${encodedLastName},%20${encodedFirstName}.png`;
};

const PlayerProfile = ({ players, fixtures, oddsData = [], selectedMatchId, isDarkMode, selectedMatchup, showHomeTeam }) => {
  const { playerId } = useParams();
  const [selectedStat, setSelectedStat] = useState('tries');
  const [statLine, setStatLine] = useState(0);
  const [hitRate, setHitRate] = useState({ percentage: 0, record: '0/0' });
  const [playerOdds, setPlayerOdds] = useState([]);
  const [groupedOdds, setGroupedOdds] = useState({});
  const [expandedOddsGroups, setExpandedOddsGroups] = useState({});
  const [customLine, setCustomLine] = useState(null);
  const [showOverOdds, setShowOverOdds] = useState(true);
  const [activeFilters, setActiveFilters] = useState([]);
  const [selectedSeasons, setSelectedSeasons] = useState([2025,2024]);
  const [selectedWithoutPlayers, setSelectedWithoutPlayers] = useState([]);
  const [showWithoutSelect, setShowWithoutSelect] = useState(false);
  const [statFilters, setStatFilters] = useState([]);
  const [showStatFilterInput, setShowStatFilterInput] = useState(false);
  const [statFilterStat, setStatFilterStat] = useState('all_run_metres');
  const [statFilterValue, setStatFilterValue] = useState('');
  const [statFilterOperator, setStatFilterOperator] = useState('>');
  const [tempStatFilter, setTempStatFilter] = useState(null);
  const [dvpRanks, setDvpRanks] = useState(null);
  const [selectedGameSpan, setSelectedGameSpan] = useState(null);
  const [includeFinals, setIncludeFinals] = useState(true);
  const [player, setPlayer] = useState(null);
  const [gamesDisplayed, setGamesDisplayed] = useState(64);
  const withoutButtonRef = useRef(null);
  const statDropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [showStatDropdown, setShowStatDropdown] = useState(false);
  const [line, setLine] = useState(20);
  
  // Find the player by name instead of ID
  const playerName = decodeURIComponent(playerId); // Decode the URL-encoded name

  // Set player when data is available
  useEffect(() => {
    if (players && players.length > 0 && playerName) {
      const foundPlayer = findPlayerByName(players, playerName);
      setPlayer(foundPlayer);
    }
  }, [players, playerName]);

  // Add useEffect for dropdown positioning
  useEffect(() => {
    if ((showWithoutSelect && withoutButtonRef.current) || (showStatDropdown && statDropdownRef.current)) {
      const buttonRect = (showWithoutSelect ? withoutButtonRef : statDropdownRef).current.getBoundingClientRect();
      document.documentElement.style.setProperty('--dropdown-top', `${buttonRect.bottom + window.scrollY + 4}px`);
      document.documentElement.style.setProperty('--dropdown-left', `${buttonRect.left + window.scrollX}px`);
    }
  }, [showWithoutSelect, showStatDropdown]);

  // Add click outside handler
  useEffect(() => {
    if (showWithoutSelect || showStatDropdown) {
      const handleClickOutside = (event) => {
        if (withoutButtonRef.current && !withoutButtonRef.current.contains(event.target) && showWithoutSelect) {
          setShowWithoutSelect(false);
        }
        if (statDropdownRef.current && !statDropdownRef.current.contains(event.target) && showStatDropdown) {
          setShowStatDropdown(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showWithoutSelect, showStatDropdown]);

  // Get unique years
  const availableSeasons = useMemo(() => {
    if (!players?.length) return [];
    return [...new Set(players.map(p => p.year))]
      .filter(Boolean)
      .sort((a, b) => b - a); // Sort descending
  }, [players]);
  
  // Set initial year when availableSeasons changes
  useEffect(() => {
    if (availableSeasons.length > 0 && selectedSeasons.length === 0) {
      setSelectedSeasons([availableSeasons[0]]); // Set to most recent year
    }
  }, [availableSeasons]);
  
  // Group odds data processing in a single useEffect
  useEffect(() => {
    if (oddsData && oddsData.length > 0) {
      console.log('Sample Odds Data:', oddsData.slice(0, 3));
      
      // Get unique players and markets
      const uniquePlayers = [...new Set(oddsData.map(odd => odd.Player))];
      const uniqueMarkets = [...new Set(oddsData.map(odd => odd.Stat))];
      
      console.log('Unique Players:', uniquePlayers);
      console.log('Unique Markets:', uniqueMarkets);

      // Get odds for current player and stat
      const newPlayerOdds = getPlayerOdds(oddsData, playerName, selectedStat);
      setPlayerOdds(newPlayerOdds);

      console.log('Player odds lookup:', {
        playerName,
        selectedStat,
        oddsFound: newPlayerOdds?.length > 0,
        playerOdds: newPlayerOdds
      });
    }
  }, [oddsData, playerName, selectedStat]); // Dependencies for odds processing

  // Add a function to handle market odds click
  const handleMarketClick = (marketLine) => {
    // Convert market line to numeric line value
    let numericLine;
    
    if (marketLine === 'FIRST' || marketLine === 'LAST') {
      numericLine = 0.5; // Set to 0.5 for FIRST/LAST markets
    } else {
      // Check if the line ends with + (e.g., "2+", "3+")
      const endsWithPlus = marketLine.includes('+');
      // Extract number from market
      const number = parseFloat(marketLine.replace(/\+/g, ''));
      // Only subtract 0.5 if it ends with +, otherwise use the number as is
      numericLine = endsWithPlus ? Math.max(number - 0.5, 0.5) : number;
    }
    
    setCustomLine(numericLine);
    setLine(numericLine);
  };

  // Update the odds grouping effect to include the click handler
  useEffect(() => {
    if (player && selectedStat) {
      // Get all player games (unfiltered) for calculating the base line
      const allPlayerGames = players.filter(p => p.player_name === playerName && p.date);
      
      // Calculate betting information based on ALL games (not filtered)
      // This will be used as fallback if no market odds are available
      const calculatedLine = Math.floor(calculateOverUnderLine(allPlayerGames, playerName, selectedStat)) + 0.5;
      setStatLine(calculatedLine);
      
      // Get odds for this player and stat
      if (oddsData && oddsData.length > 0) {
        const fullName = getPlayerFullName(player);
        const oddsStatCategory = convertStatCategoryForOdds(selectedStat);
        
        const odds = getPlayerOdds(oddsData, fullName, oddsStatCategory);
        setPlayerOdds(odds);
        
        // Group odds by line
        const grouped = {};
        odds.forEach(odd => {
          const line = odd.line;
          if (!grouped[line]) {
            grouped[line] = [];
          }
          grouped[line].push({
            ...odd,
            onClick: () => handleMarketClick(line) // Add click handler to each odd
          });
        });
        
        // Custom sort order for the grouped odds
        const sortedGrouped = {};
        
        // First add FIRST/LAST if they exist
        ['FIRST', 'LAST'].forEach(special => {
          if (grouped[special]) {
            sortedGrouped[special] = grouped[special];
          }
        });
        
        // Then add numeric lines in ascending order
        Object.keys(grouped)
          .filter(line => !['FIRST', 'LAST'].includes(line)) // Filter out FIRST/LAST
          .sort((a, b) => {
            // Remove any '+' signs and convert to numbers for comparison
            const aNum = parseFloat(a.replace(/\+/g, ''));
            const bNum = parseFloat(b.replace(/\+/g, ''));
            return aNum - bNum;
          })
          .forEach(line => {
            sortedGrouped[line] = grouped[line];
          });
        
        setGroupedOdds(sortedGrouped);
        setExpandedOddsGroups({});
        
        // Get first available market line
        const marketLines = Object.keys(sortedGrouped).filter(line => line !== 'FIRST' && line !== 'LAST');
        
        // Set the line based on first available market, or fall back to calculated average
        if (marketLines.length > 0) {
          // For market lines with "+", subtract 0.5 to get the actual line value
          const firstLine = marketLines[0];
          const endsWithPlus = firstLine.includes('+');
          const numericLine = parseFloat(firstLine.replace(/\+/g, ''));
          const adjustedLine = endsWithPlus ? Math.max(numericLine - 0.5, 0.5) : numericLine;
          
          setCustomLine(adjustedLine);
          setLine(adjustedLine);
        } else if (sortedGrouped['FIRST'] || sortedGrouped['LAST']) {
          // For FIRST/LAST markets, use 0.5 as the line
          setCustomLine(0.5);
          setLine(0.5);
        } else {
          // No market lines available, use calculated average
          setCustomLine(null);
          setLine(calculatedLine);
        }
      } else {
        // No odds data available, use calculated average
        setCustomLine(null);
        setLine(calculatedLine);
      }
    }
  }, [player, players, playerName, selectedStat, oddsData]);
  
  // Effect for filter changes - updates hit rates but doesn't change the line
  useEffect(() => {
    if (player && selectedStat) {
      // Get filtered games for hit rates
      const filteredGames = getFilteredGames(players, playerName, activeFilters);
      
      // Use line directly instead of recalculating
      const hitRateResult = calculateHitRate(filteredGames, playerName, selectedStat, line);
      setHitRate(hitRateResult);
    }
  }, [player, players, playerName, selectedStat, line, activeFilters]);
  
  // Get team players for "Without" dropdown - filtered by selected years
  const teamPlayers = useMemo(() => {
    return getTeamPlayers(players, player, selectedSeasons);
  }, [players, player, selectedSeasons]);

  // Get rounds where selected players didn't play
  const roundsWithoutPlayers = useMemo(() => {
    if (!selectedWithoutPlayers.length || !player) return null;
    
    const playerTeam = player.player_team;
    // Get all rounds for the team in selected years
    const teamRounds = [...new Set(players
      .filter(p => p.player_team === playerTeam && selectedSeasons.includes(p.year))
      .map(p => p.round))];
    
    // Get rounds where any of the selected players played
    const roundsWithPlayers = new Set(players
      .filter(p => p.player_team === playerTeam && 
                 selectedSeasons.includes(p.year) &&
                 selectedWithoutPlayers.includes(p.player_name))
      .map(p => p.round));
    
    return teamRounds.filter(round => !roundsWithPlayers.has(round));
  }, [players, player, selectedWithoutPlayers, selectedSeasons]);
  
  // Handle filter toggle
  const toggleFilter = (filterType, filterValue = null) => {
    setActiveFilters(prevFilters => {
      // Check if this filter is already active
      const existingFilterIndex = prevFilters.findIndex(f => 
        f.type === filterType && (filterType !== 'venue' && filterType !== 'weather' || f.value === filterValue)
      );
      
      if (existingFilterIndex >= 0) {
        // Filter exists, remove it
        return prevFilters.filter((_, index) => index !== existingFilterIndex);
      } else {
        // Filter doesn't exist, add it
        const newFilter = { type: filterType };
        if (filterValue) newFilter.value = filterValue;
        return [...prevFilters, newFilter];
      }
    });
  };
  
  // Check if a filter is active
  const isFilterActive = (filterType, filterValue = null) => {
    return checkFilterActive(activeFilters, filterType, filterValue);
  };
  
  // Clear all filters
  const clearAllFilters = () => {
    setActiveFilters([]);
    setSelectedWithoutPlayers([]);
    setStatFilters([]);
  };
  
  // Add year toggle handler
  const toggleSeason = (season) => {
    setSelectedSeasons(prev => {
      if (prev.includes(season)) {
        // Don't allow deselecting if it's the last selected year
        if (prev.length === 1) return prev;
        return prev.filter(s => s !== season);
      }
      return [...prev, season];
    });
  };

  // Get next fixture - Update to use most recent game's team
  const nextFixture = useMemo(() => {
    if (!player || !fixtures || !players) return null;
    
    // Get player's current team from most recent game
    const recentGame = getMostRecentGame(players, playerName);
    
    if (!recentGame) return null;
    
    const currentTeam = recentGame['team.name'];
    return fixtures.find(f => 
      !f['Winner'] && 
      (f['Home Team'] === currentTeam || f['Away Team'] === currentTeam)
    );
  }, [player, fixtures, players, playerName]);
  
  // Show loading indicator while data is being fetched
  if (!player) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }
  
  // Get most recent game for player info
  const mostRecentGame = getMostRecentGame(players, playerName);

  const fullName = getPlayerFullName(mostRecentGame || player);
  const team = getPlayerTeam(mostRecentGame || player);
  const position = player.position;
  
  // Set custom line and recalculate stats
  const handleLineChange = (newLine) => {
    setLine(newLine);
  };
  
  // Get best odds for a specific line and type
  const getBestOdds = (line, type) => {
    const matchingOdds = playerOdds.filter(odd => 
      odd.line === line && odd.type === type
    );
    
    if (matchingOdds.length === 0) return null;
    
    return matchingOdds.reduce((best, current) => {
      const currentValue = parseFloat(current.value);
      const bestValue = best ? parseFloat(best.value) : 0;
      return currentValue > bestValue ? current : best;
    }, null);
  };
  
  // Update dropdown position when button is clicked
  const updateDropdownPosition = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX
    });
  };
  
  // Add this before the return statement to construct the image URL
  const getPlayerImage = () => {
    if (!fullName) return '';
    // Split name and reverse the order
    const nameParts = fullName.split(' ');
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' '); // Handle multi-word last names
    return getPlayerImageUrl(firstName, lastName);
  };
  
  // Add handler for games displayed changes
  const handleGamesDisplayedChange = (count) => {
    setGamesDisplayed(count);
  };

  return (
    <div className="w-full px-4 pt-2 pb-6">
      {/* Player Header */}
      <PlayerHeader 
        player={player}
        mostRecentGame={mostRecentGame}
        fullName={fullName}
        team={team}
        position={position}
        selectedMatchId={selectedMatchId}
        fixtures={fixtures}
        playerImage={getPlayerImage()}
      />

      {/* Controls */}
      <PlayerControls
        availableSeasons={availableSeasons}
        selectedSeasons={selectedSeasons}
        toggleSeason={toggleSeason}
        selectedStat={selectedStat}
        setSelectedStat={setSelectedStat}
        showStatDropdown={showStatDropdown}
        setShowStatDropdown={setShowStatDropdown}
        groupedOdds={groupedOdds}
        getBestOdds={getBestOdds}
        showOverOdds={showOverOdds}
        setShowOverOdds={setShowOverOdds}
        handleLineChange={handleLineChange}
        customLine={customLine}
        includeFinals={includeFinals}
        setIncludeFinals={setIncludeFinals}
        setActiveFilters={setActiveFilters}
        onMarketClick={handleMarketClick}
      />

      {/* Hit Rates */}
      <PlayerStats
        players={players}
        playerName={playerName}
        selectedStat={selectedStat}
        line={line}
        activeFilters={activeFilters}
        selectedGameSpan={selectedGameSpan}
        setSelectedGameSpan={setSelectedGameSpan}
        getFilteredGames={getFilteredGames}
        gamesDisplayed={gamesDisplayed}
      />

      {/* Performance Chart */}
      <div className="mt-2">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden w-full">
          <StatChart
            players={players}
            playerId={player?.player_name}
            statCategory={selectedStat}
            line={line}
            onLineChange={handleLineChange}
            gameSpan={selectedGameSpan}
            isDarkMode={isDarkMode}
            activeFilters={activeFilters}
            isFilterActive={isFilterActive}
            toggleFilter={(type, value) => toggleFilter(type, value)}
            selectedSeasons={selectedSeasons}
            onGamesDisplayedChange={handleGamesDisplayedChange}
          />
        </div>
      </div>

      {/* Filters */}
      <PlayerFilters
        activeFilters={activeFilters}
        setActiveFilters={setActiveFilters}
        isFilterActive={isFilterActive}
        toggleFilter={toggleFilter}
        clearAllFilters={clearAllFilters}
        selectedWithoutPlayers={selectedWithoutPlayers}
        setSelectedWithoutPlayers={setSelectedWithoutPlayers}
        teamPlayers={teamPlayers}
        showWithoutSelect={showWithoutSelect}
        setShowWithoutSelect={setShowWithoutSelect}
        updateDropdownPosition={updateDropdownPosition}
        dropdownPosition={dropdownPosition}
        dvpRanks={dvpRanks}
        player={player}
        selectedStat={selectedStat}
        showStatFilterInput={showStatFilterInput}
        setShowStatFilterInput={setShowStatFilterInput}
        statFilterStat={statFilterStat}
        setStatFilterStat={setStatFilterStat}
        statFilterOperator={statFilterOperator}
        setStatFilterOperator={setStatFilterOperator}
        statFilterValue={statFilterValue}
        setStatFilterValue={setStatFilterValue}
        tempStatFilter={tempStatFilter}
        setTempStatFilter={setTempStatFilter}
        statFilters={statFilters}
        setStatFilters={setStatFilters}
        playerName={playerName}
        players={players}
        fixtures={fixtures}
        selectedMatchId={selectedMatchId}
        selectedSeasons={selectedSeasons}
        selectedMatchup={selectedMatchup}
        showHomeTeam={showHomeTeam}
      />

      {/* Form and Splits */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 mt-6">
        <div className="overflow-x-auto">
          <div className="lg:min-w-0">
            <PlayerForm 
              players={players}
              playerId={playerId}
              selectedStat={selectedStat}
              activeFilters={activeFilters}
              getFilteredGames={getFilteredGames}
              getStatName={getStatName}
              selectedSeasons={selectedSeasons}
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <div className="lg:min-w-0">
            <PlayerSplits 
              players={players}
              playerId={playerId}
              selectedStat={selectedStat}
              activeFilters={activeFilters}
              isFilterActive={isFilterActive}
              toggleFilter={toggleFilter}
              getStatName={getStatName}
              selectedSeasons={selectedSeasons}
            />
          </div>
        </div>
      </div>

      {/* Recent Games */}
      <div className="overflow-x-auto">
        <div className="w-full">
          <PlayerRecentGames 
            players={players}
            playerId={playerId}
            selectedStat={selectedStat}
            activeFilters={activeFilters}
            getFilteredGames={getFilteredGames}
            getStatName={getStatName}
            customLine={customLine}
            statLine={statLine}
            selectedSeasons={selectedSeasons}
          />
        </div>
      </div>
    </div>
  );
};

export default PlayerProfile; 